import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import {
  DataService as CoreDataService,
  ObjectKey,
  PersistenceService,
  PersistMode,
  PersistObjectModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryObjectsModel,
  TargetColumnValue,
  TargetObjectData,
} from '@barthauer/abc/core/data';
import { AddressPerson, Person } from '@barthauer/custom/xmt-models';
import { map, Observable } from 'rxjs';
import { DataService, ObjectKeys } from '../../services/data.service';
import { PersonStatus, ROLES } from '../../models/enumeration-list.model';
import { IRole } from './role.interface';

@Injectable({
  providedIn: 'root',
})
export class StaffMembersService {
  private readonly dataService = inject(DataService);
  public isUpdating = false;

  public readonly personColumns: QueryColumn[] = [
    this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('FirstName', 'FirstName', QueryColumnSortOrder.Ascending),
    this.coreDataService.createQueryColumn('LastName', 'LastName', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('EMail', 'EMail', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('Login', 'Login', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('Function', 'Function', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('Status', 'Status', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('Active', 'Active'),
  ];

  public readonly documentsColumns: QueryColumn[] = [
    this.coreDataService.createQueryColumn('Id', 'Id', QueryColumnSortOrder.None),
    this.coreDataService.createQueryColumn('Designation', 'Designation', QueryColumnSortOrder.None),
    {
      Name: 'Object',
      OPath: 'Object',
      Format: 'Base64Image[80x80]',
    },
  ];
  public readonly positionColumns: QueryColumn[] = [
    this.coreDataService.createQueryColumn('Id', 'Id'),
    this.coreDataService.createQueryColumn('Position', 'Position'),
    this.coreDataService.createQueryColumn('Status'),
  ];
  public positions: WritableSignal<IRole[]> = signal([]);
  public selectedPersonId = signal('');
  public trackPopup = signal(false);
  constructor(private persistenceService: PersistenceService, private coreDataService: CoreDataService) { }
  public getPersons():Observable<Person>{
    const projectSelectedOpath = sessionStorage.getItem('projectId') ? `AND Exists(ProjectPersons[ParentId='${sessionStorage.getItem('projectId')}'])`:'';
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = ObjectKeys.PERSON;
    mainQuery.Columns = this.personColumns;
    mainQuery.OPath = this.dataService.isSuperAdmin
      ? `Function <> '${ROLES.SUPER_ADMIN}'`
      : `Exists(AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem('profileAddressId')}']) ${projectSelectedOpath} AND Active <> 0 AND Not(LastName = 'Admin')`;
    const positionQuery: Query = new Query();
    positionQuery.Name = 'AddressPerson';
    positionQuery.OPath = `AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem('profileAddressId')}']`;
    positionQuery.Columns = this.positionColumns;
    mainQuery.ObjectQueries = [positionQuery];
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [mainQuery];
    return this.coreDataService.executeReadObjectsQuery(queryDocument) as unknown as Observable<Person>;
  }
  public getPersonWithImages(oPath?: string): QueryObjectsModel {
    const projectSelectedOpath = sessionStorage.getItem('projectId') ? `AND Exists(ProjectPersons[ParentId='${sessionStorage.getItem('projectId')}'])`:'';
    const mainQuery: Query = new Query();
    mainQuery.ObjectType = ObjectKeys.PERSON;
    mainQuery.Columns = this.personColumns;
    mainQuery.OPath = this.dataService.isSuperAdmin
      ? `Function <> '${ROLES.SUPER_ADMIN}'`
      : `Exists(AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem('profileAddressId')}']) ${projectSelectedOpath} AND Active <> 0 AND Not(LastName = 'Admin')`;
    if (oPath) mainQuery.OPath = oPath;
    const subQuery: Query = new Query();
    subQuery.Name = 'Documents';
    subQuery.OPath = 'Documents';
    subQuery.Columns = this.documentsColumns;
    const positionQuery: Query = new Query();
    positionQuery.Name = 'AddressPerson';
    positionQuery.OPath = `AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem('profileAddressId')}']`;
    positionQuery.Columns = this.positionColumns;
    mainQuery.ObjectQueries = [subQuery, positionQuery];
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectQueries = [mainQuery];
    return queryDocument;
  }

  public persistStafMemeberPosition = (memberPosition: AddressPerson): Observable<any> => {
    const personInternalRoleInsertObjectData: TargetObjectData = new TargetObjectData();
    personInternalRoleInsertObjectData.ObjectKey = new ObjectKey();
    personInternalRoleInsertObjectData.ObjectKey.ObjectType = ObjectKeys.ADDRESSPERSON;
    const addressPersonObjectColumns: TargetColumnValue[] = [
      { Name: 'Position', Value: memberPosition.Position },
      { Name: 'ParentId', Value: memberPosition.ParentId },
      { Name: 'IdRefPerson', Value: memberPosition.IdRefPerson },
    ];
    if (memberPosition?.Id !== undefined && memberPosition?.Id !== null) {
      personInternalRoleInsertObjectData.ObjectKey.Id = memberPosition?.Id;
      personInternalRoleInsertObjectData.Mode = PersistMode.Update;
    } else {
      personInternalRoleInsertObjectData.Mode = PersistMode.Insert;
    }
    personInternalRoleInsertObjectData.TargetColumns = addressPersonObjectColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = personInternalRoleInsertObjectData;
    return this.persistenceService.executePersistObjectQuery(persistObject);
  };

  public persistStaffMember = (staffMember: Person): Observable<any> => {
    const staffMemberPersistQuery: TargetObjectData = new TargetObjectData();
    staffMemberPersistQuery.ObjectKey = new ObjectKey();
    staffMemberPersistQuery.ObjectKey.ObjectType = ObjectKeys.PERSON;
    const staffMemberColumns: TargetColumnValue[] = [
      { Name: 'FirstName', Value: staffMember.FirstName },
      { Name: 'LastName', Value: staffMember.LastName },
      { Name: 'EMail', Value: staffMember.EMail },
      { Name: 'Login', Value: staffMember.Login },
      { Name: 'Function', Value: staffMember.Function },
      { Name: 'Status', Value: staffMember.Status },
      { Name: 'Active', Value: 1 },
    ];
    if (staffMember.Id) {
      this.isUpdating = true;
      staffMemberPersistQuery.Mode = PersistMode.Update;
      staffMemberPersistQuery.ObjectKey.Id = staffMember.Id;
    } else {
      this.isUpdating = false;
      staffMemberPersistQuery.Mode = PersistMode.Insert;
    }
    staffMemberPersistQuery.TargetColumns = staffMemberColumns;
    const persistObject: PersistObjectModel = new PersistObjectModel();
    persistObject.Object = staffMemberPersistQuery;
    return this.persistenceService.executePersistObjectQuery(persistObject);
  };


  /**
   * Checks if a Email is already used.
   *
   * @param {string} email - The login to check.
   * @returns {Observable<ArrayBuffer>} - Returns an Observable that emits `true` if the label is used, `false` otherwise.
   */
  public isEmailUsed(email: string): Observable<boolean> {
    const commentQuery: QueryObjectsModel =
      this.coreDataService.createSimpleQuery(
        '',
        ObjectKeys.PERSON,
        `EMail Like '${email}'`
      );

    return this.coreDataService.executeReadObjectsQuery(commentQuery)
      .pipe(
        map((result) => Array.isArray(result) && result.length > 0)
      );
  }
  public verifyUserExists(email: string): Observable<Person[]> {
    const personQuery: Query = new Query();
    personQuery.ObjectType = ObjectKeys.PERSON;
    personQuery.Columns = [
      this.coreDataService.createQueryColumn("Id", "Id"),
      this.coreDataService.createQueryColumn("FirstName", "FirstName"),
      this.coreDataService.createQueryColumn("LastName", "LastName"),
      this.coreDataService.createQueryColumn("Login", "Login"),
      this.coreDataService.createQueryColumn("Email", "EMail"),
      this.coreDataService.createQueryColumn("Function", "Function"),
      this.coreDataService.createQueryColumn("Status", "Status"),
      this.coreDataService.createQueryColumn("CaseNull(Active,1)","Active"),
    ];
    personQuery.OPath = `EMail Like '${email}'`;
    const AddressPersonQuery: Query = new Query();
    AddressPersonQuery.Name = 'AddressPerson';
    AddressPersonQuery.OPath = `AddressPerson_IdRefPerson_Person[ParentId='${sessionStorage.getItem('profileAddressId')}']`;
    AddressPersonQuery.Columns = [
      this.coreDataService.createQueryColumn("Id", "Id"),
      this.coreDataService.createQueryColumn("Position", "Position"),
    ];
    const projectPersonQuery: Query = new Query();
    projectPersonQuery.Name = 'ProjectPerson';
    projectPersonQuery.OPath = `ProjectPersons[ParentId='${sessionStorage.getItem('projectId')}']`;
    projectPersonQuery.Columns = [
      this.coreDataService.createQueryColumn("Id", "Id"),
    ];
    personQuery.ObjectQueries = [AddressPersonQuery,projectPersonQuery];
    const mainQuery: QueryObjectsModel = new QueryObjectsModel();
    mainQuery.ObjectQueries = [personQuery];
    return this.coreDataService.executeReadObjectsQuery(mainQuery) as unknown as Observable<Person[]>;
  }
  public archiveOrRestorePerson(userId: string,isActive:boolean):Observable<ArrayBuffer>{
    const columns: TargetColumnValue[] = [
      {
        Name:'Active',
        Value: !isActive
      }
    ];
    return this.dataService.persistObject(PersistMode.Update,ObjectKeys.PERSON,columns,userId);
  }
  public archiveOrRestoreAddressPerson(userId: string,status:string):Observable<ArrayBuffer>{
    const columns: TargetColumnValue[] = [
      {
        Name:'Status',
        Value: this.reverseStatus(status) 
      }
    ];
    return this.dataService.persistObject(PersistMode.Update,ObjectKeys.ADDRESSPERSON,columns,userId);
  }
  public reverseStatus(status:string):string{
    return status === PersonStatus.ARCHIVED
     ? PersonStatus.ACTIVE
     : PersonStatus.ARCHIVED
  }
  public createProjectPerson(personId:string,projectId:string):Observable<ArrayBuffer>{
    const columns : TargetColumnValue[]=[
      this.coreDataService.createPersistTargetColumn('IdRefPerson',personId),
      this.coreDataService.createPersistTargetColumn('ParentId',projectId),
    ]
    return this.dataService.persistObject(PersistMode.Insert,ObjectKeys.PROJECT_PERSON,columns);
  }
}
